import { useEffect, useState, useCallback } from 'react';
import { User } from 'lucide-react';

const STORAGE_KEY = 'propveiw.myTasksFilter';

/**
 * useMyTasksFilter — persisted per-browser boolean toggle that
 * controls whether views hide tasks the current user isn't
 * assigned-to or shared-with.
 */
export const useMyTasksFilter = () => {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (err) {
      console.debug('localStorage read failed for myTasksFilter:', err?.message);
      return false;
    }
  });
  const toggle = useCallback((val) => {
    const next = typeof val === 'boolean' ? val : !enabled;
    setEnabled(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch (err) {
      console.debug('localStorage write failed for myTasksFilter:', err?.message);
    }
  }, [enabled]);
  return [enabled, toggle];
};

/**
 * `isMineForTask` — the single source of truth for "is this task mine?"
 * across CleaningView / MaintenanceView / RenovationsView / CalendarView.
 *
 * A task belongs to the user if:
 *   - user is the assigned worker, OR
 *   - user is listed in collaborator_ids.
 *
 * When the backend has masked the task (`_restricted: true`), the
 * `assigned_to` and `collaborator_ids` fields are stripped — such tasks
 * are by definition NOT the user's, so we return false.
 */
export const isMineForTask = (task, userId) => {
  if (!task || !userId) return false;
  if (task._restricted) return false;
  if (task.assigned_to && task.assigned_to === userId) return true;
  const collabs = task.collaborator_ids || [];
  return collabs.includes(userId);
};

/**
 * `isMineForEvent` — calendar-event variant. When the "My tasks" filter
 * is on, we hide everything the user isn't personally responsible for —
 * including bookings and owner-use blocks. A calendar event counts as
 * "mine" if the event isn't restricted AND the `details.assigned_to`
 * matches this user.
 *
 * Backend caveat: `collaborator_ids` isn't exposed on calendar events,
 * but restricted events already imply "not mine" — an unrestricted task
 * event you're seeing but aren't `assigned_to` means you're a
 * collaborator OR a manager. For managers this could hide too much, so
 * managers should typically leave the toggle off.
 */
export const isMineForEvent = (event, userId) => {
  if (!event || !userId) return false;
  if (event.details?._restricted) return false;
  const assigned = event.details?.assigned_to;
  if (assigned && assigned === userId) return true;
  const collabs = event.details?.collaborator_ids || [];
  return collabs.includes(userId);
};

export const MyTasksToggle = ({ enabled, onChange, count = null, testId = 'my-tasks-toggle' }) => {
  // Keep any external listener reacting to storage-driven changes
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && typeof onChange === 'function') {
        onChange(e.newValue === '1');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [onChange]);

  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
        enabled
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
      }`}
      data-testid={testId}
      aria-pressed={enabled}
      title="Show only tasks assigned to me or shared with me"
    >
      <User className="h-3.5 w-3.5" />
      <span>My tasks</span>
      {count != null && (
        <span
          className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${
            enabled ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {count}
        </span>
      )}
      <span
        aria-hidden="true"
        className={`ml-1 inline-flex h-3 w-6 items-center rounded-full transition-colors ${
          enabled ? 'bg-white/25' : 'bg-slate-200'
        }`}
      >
        <span
          className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow transition-transform ${
            enabled ? 'translate-x-3' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
};

export default MyTasksToggle;
